/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react';
import { connect } from 'react-redux';
import cx from 'classnames';
import { Redirect } from 'react-router-dom';
import format from 'date-fns/format';
import reactStringReplace from 'react-string-replace';

// lodash
import startCase from 'lodash/startCase';

import { Text } from '@zillow/constellation';

// Multibrand-UI Components
import Notification from '@zg-rentals/multibrand-ui/lib/Notification';
import ButtonGroup from '@zg-rentals/multibrand-ui/lib/ButtonGroup';
import Button from '@zg-rentals/multibrand-ui/lib/Button';

// awesome styled grid
import {
    Link,
    Container,
    Row,
    Col,
} from '@zillow/zrs-comet';

// rmw context
import { RMWContext } from 'app/shared/providers/RMWContext';

// Actions
import ApplicationActions from 'app/shared/actions/ApplicationActions';
import EmailVerificationActions from 'app/shared/actions/EmailVerificationActions';
import ModalActions from 'app/shared/actions/ModalActions';
import PropertiesActions from 'app/shared/actions/PropertiesActions';
import TermsAndConditionsActions from 'app/shared/actions/TermsAndConditionsActions';

// containers
import AuthCheckWrapper from 'app/shared/containers/AuthCheckWrapper';

// components
import BannerMaintenance from 'app/shared/components/BannerMaintenance';
import { withErrorBoundary } from 'app/shared/components/ErrorBoundary';

// Selectors
import {
    AppSelectors,
    EmailVerificationSelectors,
    ModalSelectors,
    PropertiesSelectors,
    RoutingSelectors,
    UserSelectors,
} from 'app/shared/selectors';

// Modals
import {
    AddPropertyModal,
    EnableApplicationsModal,
    VerifyEmailFailureModal,
    VerifyEmailModal,
} from 'app/shared/components/Modal';

import Constants from 'app/shared/constants';
import StringConstants from 'app/shared/constants/StringConstants';
import PropertiesConstants from 'app/shared/constants/PropertiesConstants';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import QueryConstants from 'app/shared/constants/QueryConstants';
import HeadWrapper from 'app/shared/components/HeadWrapper';
import InfiniteScroller from 'app/shared/components/InfiniteScroller';
import pathsManifest from 'app/shared/pathsManifest';
import PropertiesDisplay from 'app/shared/components/properties/PropertiesDisplay';
import queryUtils from 'app/shared/utils/queryUtils';
import PendingEmailVerificationBox from 'app/shared/components/properties/PendingEmailVerificationBox';
import ActionMessageBox from 'app/shared/components/properties/ActionMessageBox';
import PropertiesPageSkeleton from 'app/shared/components/skeletons/PropertiesPageSkeleton';

import SearchBox from 'app/shared/components/SearchBox';

// utils
import accessUtils from 'app/shared/utils/accessUtils';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import contextUtils from 'app/shared/utils/contextUtils';
import userUtils from 'app/shared/utils/userUtils';
import numberUtils from 'app/shared/utils/numberUtils';

// notification
import NotificationModule from 'app/client/workers/notificationModule';

// styles
import {
    StyledFlexContainer,
    StyledHeading,
    StyledHeadingContainer,
    StyledIconPlusCircle,
    StyledInheritText,
    StyledLink,
    StyledSearchBoxContainer,
} from './StyledComponents';

import './style.scss';

const defaultFilterData = {
    active: Constants.PROPERTIES_ACTIVE_DEFAULT,
    inactive: Constants.PROPERTIES_INACTIVE_DEFAULT,
    searchText: Constants.PROPERTIES_SEARCHTEXT_DEFAULT,
    feeds: Constants.PROPERTIES_FEEDS_DEFAULT,
    manual: Constants.PROPERTIES_MANUAL_DEFAULT,
    ascending: Constants.PROPERTIES_ASCENDING_DEFAULT,
    sort: Constants.PROPERTIES_SORT_DEFAULT,
    featured: Constants.PROPERTIES_FEATURED_DEFAULT,
    startKeyExclusive: Constants.PROPERTIES_START_KEY_EXCLUSIVE_DEFAULT,
    limit: Constants.PROPERTIES_LIMIT_DEFAULT,
    includeArchived: Constants.PROPERTIES_INCLUDE_ARCHIVED,
    includeUnarchived: Constants.PROPERTIES_INCLUDE_UNARCHIVED,
};

const MIN_PROPERTIES_FOR_SEARCH_DISPLAY = 5;

const DEFAULT_UPDATE = {
    includeArchived: Constants.PROPERTIES_INCLUDE_ARCHIVED,
    includeUnarchived: Constants.PROPERTIES_INCLUDE_UNARCHIVED,
    searchText: Constants.PROPERTIES_SEARCHTEXT_DEFAULT,
    startKeyExclusive: Constants.PROPERTIES_START_KEY_EXCLUSIVE_DEFAULT,
};

class PropertiesPage extends React.Component {
    static contextType = RMWContext;

    constructor(props) {
        super(props);

        const {
            emailVerification,
            location,
        } = props;

        const queryParams = queryUtils.parse(location.search);

        // initial state
        this.state = {
            destinationTab: pathsManifest.getAllPaths().propertyDetailsListing,
            displayApplicationsNotification: false,
            displayEmailVerificationNotification: false,
            displayPaymentsNotification: false,
            displayLeasesNotification: false,
            displayPendingEmailVerificationBox: emailVerification.hasPendingEmailVerification,
            listingsLoading: true,
            totalListingsWithoutFilters: null,
            queryParams,
            isDebugMode: queryParams.debug === 'true',
            emailVerificationNotificationText: '',
            displayTimedOutMessage: false,
            displayNoListingsFoundMessage: true,
            disableLoadMore: false,
            displayListingDeactivatedBox: false,
            didPropertiesLoad: false,
            isSearchBarTouched: false,
            searchText: '',
            startKeyExclusive: '',
            viewType: PropertiesConstants.PROPERTIES_VIEW_TYPE.GRID,
        };

        // bind methods
        this.checkNotificationDisplays = this.checkNotificationDisplays.bind(this);
        this.checkInitialDisplayModal = this.checkInitialDisplayModal.bind(this);
        this.handleEnableApplicationsBtnClick = this.handleEnableApplicationsBtnClick.bind(this);
        this.handleNotificationCloseClick = this.handleNotificationCloseClick.bind(this);
        // eslint-disable-next-line max-len
        this.handleEmailVerificationNotificationCloseClick = this.handleEmailVerificationNotificationCloseClick.bind(this);
        this.handleLoadMoreClick = this.handleLoadMoreClick.bind(this);
        this.handlePaymentsNotificationCloseClick = this.handlePaymentsNotificationCloseClick.bind(this);
        this.handleResendEmailClick = this.handleResendEmailClick.bind(this);
        this.handleAddPropertyClick = this.handleAddPropertyClick.bind(this);
        this.handlePushNotificationClick = this.handlePushNotificationClick.bind(this);
        this.handleLeasesNotificationCloseClick = this.handleLeasesNotificationCloseClick.bind(this);
        this.onSearchFilterUpdate = this.onSearchFilterUpdate.bind(this);
        this.renderLoadComplete = this.renderLoadComplete.bind(this);
        this.renderListingsLoadComplete = this.renderListingsLoadComplete.bind(this);
        this.renderCovid19Resources = this.renderCovid19Resources.bind(this);
        this.getConversionPercentage = this.getConversionPercentage.bind(this);
        this.showAddPropertyModal = this.showAddPropertyModal.bind(this);
        this.showVerifyEmailModal = this.showVerifyEmailModal.bind(this);
        this.showVerifyEmailFailureModal = this.showVerifyEmailFailureModal.bind(this);
        this.updatePaymentsNotificationDisplay = this.updatePaymentsNotificationDisplay.bind(this);
        this.updateLeasesNotificationDisplay = this.updateLeasesNotificationDisplay.bind(this);
        this.handleCovid19ReadOnUrlClick = this.handleCovid19ReadOnUrlClick.bind(this);
        // archived
        this.toggleArchivedView = this.toggleArchivedView.bind(this);
        this.getListings = this.getListings.bind(this);
    }

    componentWillMount() {
        if (__SERVER__) {
            return;
        }
        const {
            dispatch,
            isUserBlocked,
        } = this.props;

        const {
            queryParams,
        } = this.state;

        dispatch(PropertiesActions.updateFilters({
            ...defaultFilterData,
            includePaidInclusionReleaseDates: true,
            includePaidInclusionExistingStatesUpdate: true,
        }));

        const securityToken = queryParams[QueryConstants.SECURITY_TOKEN];

        if (!isUserBlocked && securityToken) {
            dispatch(EmailVerificationActions.verifySecurityToken(securityToken))
                .then((response) => {
                    const apiResponse = response.response;
                    let emailVerificationNotificationText = 'Your email has been verified!';

                    /**
                     *  When successful:
                     *  {
                     *      status: 200,
                     *      success: true
                     *  }
                     *
                     *  On failure:
                     *  {
                     *      status: 400,
                     *      success: false,
                     *      response: {
                     *          errorCode: 'email-already-verified',
                     *          message: 'Your email has already been verified'
                     *      }
                     *  }
                     */
                    if (apiResponse &&
                        apiResponse.errorCode &&
                        apiResponse.errorCode === 'email-already-verified') {
                        emailVerificationNotificationText = apiResponse.message;
                    }

                    this.setState({
                        displayEmailVerificationNotification: true,
                        displayPendingEmailVerificationBox: false,
                        emailVerificationNotificationText,
                    });

                    return this.getListings(true);
                })
                .catch((err) => {
                    global.TEMPORARY_global_logger.error(err, 'Failed to send verify email security token');

                    this.showVerifyEmailFailureModal();
                    return this.getListings(true);
                });
        } else {
            /*
                * get the correct properties for user based on the url
                * if archived is true in query params, fetch archived properties and set the component
                * to show archived title
            */

            const update = Object.assign({}, DEFAULT_UPDATE);

            if (queryParams.searchText) {
                update.searchText = queryParams.searchText;
                this.setState({
                    searchText: queryParams.searchText,
                    isSearchBarTouched: true,
                });
            }

            // check for archived query string
            if (queryParams.archived === 'true') {
                update.includeArchived = true;
                update.includeUnarchived = false;

                this.updateFiltersAndListings(update);
            } else {
                update.includeArchived = false;
                update.includeUnarchived = true;

                this.updateFiltersAndListings(update);
            }

            this.getListings(true, !isUserBlocked);
        }
    }

    componentDidMount() {
        const { dispatch, isUserBlocked } = this.props;

        // always close the add property modal
        dispatch(ModalActions.updateDisplayAddPropertyModal({ isOpen: false }));

        // check if user is blocked
        if (!isUserBlocked) {
            this.checkNotificationDisplays();
        }

        this._isMounted = true;

        dispatch(PropertiesActions.getListingConversionStatus());
    }

    componentWillReceiveProps(nextProps) {
        const { location, isScreenTabletOrDesktop } = this.props;
        const { isSearchBarTouched, viewType } = this.state;

        if (location.search !== nextProps.location.search) {
            const parsedQueryString = queryUtils.parse(nextProps.location.search);
            const { archived = false, startKeyExclusive = '', searchText = '' } = parsedQueryString;

            if (!searchText && !isSearchBarTouched) {
                if (this.SearchBoxInstance) {
                    this.setState({
                        searchText: '',
                        isSearchBarTouched: false,
                    });
                    this.SearchBoxInstance.updateSearchText('');
                }
            } else if (this.SearchBoxInstance) {
                this.setState({
                    searchText,
                    isSearchBarTouched: true,
                });
                this.SearchBoxInstance.updateSearchText(searchText);
            }

            const update = {
                includeArchived: archived,
                includeUnarchived: !archived,
                searchText,
                startKeyExclusive,
            };
            this.updateFiltersAndListings(update);
        }

        if (isScreenTabletOrDesktop !== nextProps.isScreenTabletOrDesktop
            && !nextProps.isScreenTabletOrDesktop
            && viewType === PropertiesConstants.PROPERTIES_VIEW_TYPE.LIST) {
            this.setState({ viewType: PropertiesConstants.PROPERTIES_VIEW_TYPE.GRID });
        }
    }

    // check if we should display notification banner
    checkNotificationDisplays() {
        const { location } = this.props;
        const { search } = location;
        const isFromMarketingPaymentsRoute = contextUtils.isFromMarketingPaymentsRoute(search);
        const isFromMarketingLeasesRoute = contextUtils.isFromMarketingLeasesRoute(search);

        if (isFromMarketingPaymentsRoute) {
            this.updatePaymentsNotificationDisplay(true);
        } else if (isFromMarketingLeasesRoute) {
            this.updateLeasesNotificationDisplay(true);
        }
    }

    checkInitialDisplayModal() {
        const { brand } = this.context;
        const { totalListingsWithoutFilters, queryParams } = this.state;
        const {
            cookieEnabledServices,
            dispatch,
            properties,
            emailVerification,
            roles,
            location,
        } = this.props;

        const { search } = location;
        const paths = pathsManifest.getAllPaths();

        const { hasFeedListings, hasPendingEmailVerification } = emailVerification;
        const isPostingPathAdmin = userUtils.isPostingPathAdmin(roles);

        const isFromMarketingPaymentsRoute = contextUtils.isFromMarketingPaymentsRoute(search);
        const isFromMarketingApplicationsRoute = contextUtils.isFromMarketingApplicationsRoute(search);
        const isFromMarketingLeasesRoute = contextUtils.isFromMarketingLeasesRoute(search);

        // if any marketing campaign
        if (isFromMarketingPaymentsRoute || isFromMarketingApplicationsRoute || isFromMarketingLeasesRoute) {
            let marketingRoute = pathsManifest.getAllPaths().propertyDetailsListing;
            if (isFromMarketingPaymentsRoute) {
                marketingRoute = Constants.MARKETING_PATH.PAYMENTS;
            } else if (isFromMarketingApplicationsRoute) {
                marketingRoute = Constants.MARKETING_PATH.APPLICATIONS;
            } else if (isFromMarketingLeasesRoute) {
                marketingRoute = Constants.MARKETING_PATH.LEASES;
            }

            this.setState({ destinationTab: marketingRoute });

            // if total listings = 0
            if (totalListingsWithoutFilters === 0) {
                this.showAddPropertyModal();

                // track
                const dimension = AnalyticsConstants.Z_CUSTOM_DIMENSION.EVENT_CONTENT;
                analyticsUtils.setCustomDimension(`MarketingPath${startCase(marketingRoute)}`, dimension, brand);
                dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.ADD_PROPERTY_MODAL));
                return;
            }

            // if 1 listing go to PDP (app or payments)
            if (totalListingsWithoutFilters === 1) {
                const { listingId } = properties.listings[0];
                const targetRoute = `${paths.propertiesPage}/${listingId}/${marketingRoute}${location.search}`;

                // nav to pdp
                window.location = targetRoute;
                return;
            }

            // if total listings > 1 and from applications campaign
            if (isFromMarketingApplicationsRoute && totalListingsWithoutFilters > 1) {
                // check if any listing can access applications
                let foundListingWithApplicationAccess = false;

                for (let i = 0; i < properties.listings.length; i++) {
                    const { restrictions } = properties.listings[i];

                    const canAccessApplicationsResponse = accessUtils.canAccessApplications({
                        brand,
                        restrictions,
                        cookieEnabledServices,
                        groupState: properties.listings[i].state,
                    });

                    if (canAccessApplicationsResponse) {
                        foundListingWithApplicationAccess = true;
                        break;
                    }
                }

                // found a listing
                if (foundListingWithApplicationAccess) {
                    dispatch(ModalActions.updateDisplayEnableApplicationsModal({ isOpen: true }));
                    return;
                }
            }
        }

        // if coming from zillow landing page or hotpads post a listing link
        if (queryParams[QueryConstants.POSTING_PATH] === 'true'
            || (queryParams[QueryConstants.SIGN_IN_LP] === 'true'
            && totalListingsWithoutFilters === 0)) {
            this.showAddPropertyModal();

            // track only if coming from Get Started button on landing page
            if (queryParams[QueryConstants.POSTING_PATH] === 'true' &&
                brand === Constants.UNFORMATTED_BRAND_NAMES.zillow
            ) {
                const dimension = AnalyticsConstants.Z_CUSTOM_DIMENSION.EVENT_CONTENT;
                analyticsUtils.setCustomDimension('LandingPageGetStarted', dimension, brand);

                dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.ADD_PROPERTY_MODAL));
            }
            return;
        }

        // if has feed listings and should NOT see add property modal
        //     (Add property modal has higher priority. The user will see
        //     the email verification modal to see feed listings next time they see
        //     properties page)
        if (queryParams[QueryConstants.EMAIL_VERIFICATION] === 'true' ||
            (hasFeedListings && !hasPendingEmailVerification)) {
            if (isPostingPathAdmin) {
                this.showVerifyEmailModal();
                this.setState({
                    displayPendingEmailVerificationBox: true,
                });
            } else if (!hasPendingEmailVerification) {
                dispatch(EmailVerificationActions.sendSecurityToken({ emailVerificationType: 'claimFeedListings' }))
                    .then((response) => {
                        if (response.success) {
                            dispatch(EmailVerificationActions.updateEmailVerificationInfo({
                                hasPendingEmailVerification: true,
                            }));

                            this.showVerifyEmailModal();
                            this.setState({
                                displayPendingEmailVerificationBox: true,
                            });
                        }
                    })
                    .catch((err) => {
                        global.TEMPORARY_global_logger.error(err, 'Failed to send email verification security token');
                    });
            }
            return;
        }
        if (queryParams[QueryConstants.LISTING_DEACTIVATED] === 'true') {
            this.setState({
                displayListingDeactivatedBox: true,
            });
        }
    }

    getListingsCallTimeout(response = {}) {
        let isTimeout = false;

        if (response.error) {
            const error = response.error.toString();

            isTimeout = error.indexOf('timeout') !== -1;
        }

        return isTimeout;
    }

    getListings(isFirstFetch = false, checkInitialDisplayModal = false, appendToListings = false) {
        const { dispatch } = this.props;

        return dispatch(PropertiesActions.getListings(appendToListings))
            .then((response) => {
                this.setState({
                    listingsLoading: false,
                    displayTimedOutMessage: this.getListingsCallTimeout(response),
                    didPropertiesLoad: true,
                    disableLoadMore: false,
                });

                if (isFirstFetch && response.payload) {
                    const { listingsModel } = response.payload;

                    this.setState({
                        totalListingsWithoutFilters: listingsModel.numTotalListings,
                    }, () => {
                        // complete
                        if (checkInitialDisplayModal) {
                            this.checkInitialDisplayModal();
                        }
                    });
                }
            })
            .catch((err) => {
                global.TEMPORARY_global_logger.error(err, 'Failed to get Properties');
                this.setState({
                    listingsLoading: false,
                    error: err,
                    didPropertiesLoad: false,
                    disableLoadMore: true,
                });
            });
    }

    isZillow() {
        const { brand } = this.context;
        return brand === StringConstants.UNFORMATTED_BRAND_NAMES.zillow;
    }

    onSearchFilterUpdate(value) {
        const { propertiesFilters: { includeArchived } } = this.props;
        const update = {};

        update.searchText = value;
        update.startKeyExclusive = '';

        // this is passed to let createQueryObjectAndRedirectTo know that it is a search operation
        // and bypass the router transition
        const isSearchFilterUpdate = true;

        // set state to preserve the search bar on the UI
        // if isSearchBarTouched is true it will always show search bar
        this.setState({
            isSearchBarTouched: true,
            searchText: value,
        });

        this.createQueryObjectAndRedirectTo(includeArchived, value, '', isSearchFilterUpdate);
    }

    toggleArchivedView() {
        const { propertiesFilters } = this.props;
        const { isSearchBarTouched } = this.state;
        const paths = pathsManifest.getAllPaths();

        // if showArchive is true, append filter params to the url
        const { includeArchived, includeUnarchived } = propertiesFilters;
        const route = includeArchived ? '' : '?archived=true';
        window.router.transitionTo(
            `${paths.propertiesPage}${route}`,
        );

        const update = {
            includeArchived: !includeArchived,
            includeUnarchived: !includeUnarchived,
            searchText: '', // reset the search string
            startKeyExclusive: '',
        };

        this.setState({
            isSearchBarTouched,
        });

        // clear searchbox text
        if (this.SearchBoxInstance) {
            this.SearchBoxInstance.updateSearchText('');
        }

        this.updateFiltersAndListings(update);
    }

    showAddPropertyModal() {
        const { dispatch, user } = this.props;
        if (user.features.canAddListing) {
            dispatch(ModalActions.updateDisplayAddPropertyModal({ isOpen: true }));
        }
    }

    showVerifyEmailModal() {
        const { dispatch } = this.props;
        dispatch(ModalActions.updateDisplayVerifyEmailModal({ isOpen: true }));
    }

    showVerifyEmailFailureModal() {
        const { dispatch } = this.props;
        dispatch(ModalActions.updateDisplayVerifyEmailFailureModal({ isOpen: true }));
    }

    updateFiltersAndListings(
        update,
        appendToListings = false,
        displayNoListingsFoundMessage = true,
        showLoading = true,
        disableLoadMore = false
    ) {
        const { dispatch } = this.props;
        this.setState({
            listingsLoading: showLoading,
            didPropertiesLoad: !showLoading,
            disableLoadMore,
            displayNoListingsFoundMessage,
        });

        dispatch(PropertiesActions.updateFilters(update));

        if (this._isMounted) {
            this.getListings(false, false, appendToListings);
        }
    }

    updatePaymentsNotificationDisplay(doShow = true) {
        this.setState({ displayPaymentsNotification: doShow });
    }

    updateLeasesNotificationDisplay(doShow = true) {
        this.setState({ displayLeasesNotification: doShow });
    }

    handleEnableApplicationsBtnClick() {
        const { dispatch } = this.props;

        // accept terms
        dispatch(TermsAndConditionsActions.markAcceptedTerms());

        dispatch(ApplicationActions.enableApplications()).then(() => {
            dispatch(TermsAndConditionsActions.markAcceptedTerms());
            dispatch(ModalActions.updateDisplayEnableApplicationsModal({ isOpen: false }));
            this.setState({
                displayApplicationsNotification: true,
            });
        });
    }

    handleAddPropertyClick() {
        const { dispatch, isUserBlocked } = this.props;
        const { brand } = this.context;

        if (!isUserBlocked) {
            this.showAddPropertyModal();

            // track
            if (brand === Constants.UNFORMATTED_BRAND_NAMES.zillow) {
                const dimension = AnalyticsConstants.Z_CUSTOM_DIMENSION.EVENT_CONTENT;
                analyticsUtils.setCustomDimension('MyPropertiesAddProperty', dimension, brand);

                dispatch(analyticsUtils.track.GAEventV2(
                    AnalyticsConstants.GA_EVENTS.ADD_PROPERTY_MODAL,
                ));
            }
        }
    }

    handleNotificationCloseClick() {
        this.setState({ displayApplicationsNotification: false });
    }

    handleEmailVerificationNotificationCloseClick() {
        this.setState({ displayEmailVerificationNotification: false });
    }

    handleLoadMoreClick(startKeyExclusive) {
        const {
            propertiesFilters: { includeArchived, searchText },
        } = this.props;

        const update = {
            includeArchived,
            includeUnarchived: !includeArchived,
            searchText,
            startKeyExclusive,
        };
        this.updateFiltersAndListings(update, true, false, false, true);
    }

    handleCovid19ReadOnUrlClick() {
        const { dispatch } = this.props;

        dispatch(analyticsUtils.track.GAEventV2(
            AnalyticsConstants.GA_EVENTS.BANNER_COVID19_READ_ON_CLICK
        ));
    }

    createQueryObjectAndRedirectTo(
        includeArchived = false,
        searchText = '',
        startKeyExclusive = '',
        isSearchFilterUpdate = false
    ) {
        const queryString = this.createQueryString(includeArchived, searchText, startKeyExclusive);

        // replace the url only if its a search filter update
        if (isSearchFilterUpdate) {
            window.router.redirectTo({
                pathname: this.props.location.pathname,
                search: queryString,
            });
        }
    }

    createQueryString(includeArchived = false, searchText = '', startKeyExclusive = '') {
        const queryObject = {};

        if (includeArchived) {
            queryObject.archived = includeArchived;
        }
        queryObject.startKeyExclusive = startKeyExclusive;
        queryObject.searchText = searchText;

        return queryUtils.stringify(queryObject);
    }

    handlePaymentsNotificationCloseClick() {
        this.updatePaymentsNotificationDisplay(false);
    }

    handleLeasesNotificationCloseClick() {
        this.updateLeasesNotificationDisplay(false);
    }

    handleResendEmailClick(text) {
        this.setState({
            emailVerificationNotificationText: text,
            displayEmailVerificationNotification: true,
            displayPendingEmailVerificationBox: false,
        });
    }

    getFullAddress(queryParams) {
        const {
            street = '',
            city = '',
            state = '',
            zip = '',
        } = queryParams;

        const fullAddress = `${street} ${city}, ${state} ${zip}`;

        return fullAddress.replace(/\s{2,}/g, ' ').trim();
    }

    needsRedirect() {
        const { emailVerification: { hasFeedListings, hasPendingEmailVerification } } = this.props;
        const { totalListingsWithoutFilters, queryParams } = this.state;

        const bypassRedirect = hasFeedListings && hasPendingEmailVerification;

        if (!this.isZillow() || totalListingsWithoutFilters !== 0 || bypassRedirect) {
            return false;
        }

        return (
            queryParams[QueryConstants.POSTING_PATH] !== 'true' &&
            queryParams[QueryConstants.SIGN_IN_LP] !== 'true' &&
            queryParams[QueryConstants.EMAIL_VERIFICATION] !== 'true' &&
            !queryParams[QueryConstants.MARKETING_PATH] &&
            !queryParams[QueryConstants.SECURITY_TOKEN] &&
            !queryParams[QueryConstants.ARCHIVED]
        );
    }

    renderMaintenanceBanner() {
        const { dispatch, maintenancePartner } = this.props;
        const { details } = maintenancePartner;

        return (
            <Container className="PropertiesPage-display-container MaintenanceBanner-container">
                <Row>
                    <Col>
                        <BannerMaintenance
                            details={details}
                            dispatch={dispatch}
                        />
                    </Col>
                </Row>
            </Container>
        );
    }

    renderPageHeading() {
        const {
            properties: { numTotalListings: totalPropertiesShown },
            propertiesFilters: { includeArchived },
            user,
        } = this.props;
        const { isSearchBarTouched } = this.state;

        const pageTitle = includeArchived ? 'Archived Properties' : 'Properties';

        return (
            <StyledHeadingContainer
                className={cx('PropertiesPage-heading', {
                    'PropertiesPage-heading-zg': this.isZillow(),
                })}
                fluidMobile={true}
                paddingTop="xl"
                marginBottom={2}
            >
                {(totalPropertiesShown > 0 || isSearchBarTouched) && (
                    <Row
                        align="center"
                        justify="space-between"
                    >
                        <Col className="PropertiesPage-heading-col PropertiesPage-heading-col-title">
                            <StyledHeading
                                level="3"
                                marginRight="sm"
                                serif={true}
                            >
                                {pageTitle}
                            </StyledHeading>
                        </Col>

                        <Col
                            className="PropertiesPage-heading-col PropertiesPage-heading-col-addproperty"
                            align="flex-end"
                        >
                            {user.features.canAddListing && (
                                <StyledLink
                                    type="primary"
                                    to="#"
                                    onClick={this.handleAddPropertyClick}
                                    className="PropertyCard-heading-add-property"
                                >
                                    <StyledIconPlusCircle />
                                    <StyledInheritText fontType="smallbody">Add a property</StyledInheritText>
                                </StyledLink>
                            )}
                        </Col>
                    </Row>
                )}
            </StyledHeadingContainer>
        );
    }

    renderSubHeadingSection() {
        const { isSearchBarTouched, searchText, viewType } = this.state;
        const {
            properties: {
                numActiveListings,
                numInactiveListings,
                numTotalListings: totalPropertiesShown,
            },
            isScreenTabletOrDesktop,
            isMultifamilyCustomer,
            isRocketshipCustomer,
        } = this.props;

        const summary = `${numActiveListings} for rent, ${numInactiveListings} off market`;
        const isGridView = viewType === PropertiesConstants.PROPERTIES_VIEW_TYPE.GRID;
        const showViewTypeToggle = isScreenTabletOrDesktop && (isMultifamilyCustomer || isRocketshipCustomer);

        return (
            <Container
                fluidMobile={true}
                className="SubHeadingSection"
                marginBottom="md"
            >
                { (isSearchBarTouched || totalPropertiesShown >= MIN_PROPERTIES_FOR_SEARCH_DISPLAY) && (
                    <StyledFlexContainer
                        className="SubHeadingSection-Row"
                        display="flex"
                        alignItems="center"
                        justifyContent="space-evenly"
                        paddingX="sm"
                        paddingY="sm"
                    >
                        <Text
                            className="SubHeadingSection-Summary"
                            fontType="label"
                            as="div"
                        >
                            {summary}
                        </Text>

                        <StyledSearchBoxContainer>
                            <SearchBox
                                className="SubHeaderingSection-SearchBox"
                                ref={(instance) => {
                                    this.SearchBoxInstance = instance;
                                }}
                                placeholder="Search properties"
                                onSearch={this.onSearchFilterUpdate}
                                initialText={searchText}
                            />
                        </StyledSearchBoxContainer>
                        {showViewTypeToggle && (
                            <ButtonGroup>
                                <Button
                                    full={true}
                                    onClick={() => {
                                        this.setState({ viewType: PropertiesConstants.PROPERTIES_VIEW_TYPE.GRID });
                                    }}
                                    type={isGridView ? 'primary' : 'outline'}
                                >
                                    Grid
                                </Button>
                                <Button
                                    full={true}
                                    onClick={() => {
                                        this.setState({ viewType: PropertiesConstants.PROPERTIES_VIEW_TYPE.LIST });
                                    }}
                                    type={isGridView ? 'outline' : 'primary'}
                                >
                                    List
                                </Button>
                            </ButtonGroup>
                        )}
                    </StyledFlexContainer>
                )}
            </Container>
        );
    }

    handlePushNotificationClick(event) {
        const button = event.target;

        button.disabled = true;
        button.innerHTML = NotificationModule.isSubscribed() ? '\u2611 Push Notification' : '\u2610 Push Notification';

        // check if subscriptions are registered
        if (NotificationModule.isSubscribed()) {
            // yes subscriptions - unsubscribe
            NotificationModule.unsubscribe()
                .then(() => {
                    // update ui
                    button.innerHTML = '\u2610 Push Notification';
                    button.disabled = false;
                });
        } else {
            // no subscriptions - subscribe
            NotificationModule.subscribe()
                .then(() => {
                    // update ui
                    button.innerHTML = '\u2611 Push Notification';
                    button.disabled = false;
                })
                .catch((ErrorRMW) => {
                    global.TEMPORARY_global_logger.error('ErrorRMW', ErrorRMW);
                });
        }
    }

    renderPushNotificationBtn() {
        let btnTitle = '';
        let isBtnDisabled = false;

        if (NotificationModule.hasNotificationFeature()) {
            // Note: Notification.permission is undefined at runtime.
            if (Notification.permission === 'denied') {
                // with out checkmark
                btnTitle = '\u2610 Push Notification (denied)';
                isBtnDisabled = true;
            } else if (NotificationModule.isSubscribed()) {
                // with checkmark
                btnTitle = '\u2611 Push Notification';
            } else {
                // with out checkmark
                btnTitle = '\u2610 Push Notification';
            }

            return (
                <button
                    type="button"
                    disabled={isBtnDisabled}
                    onClick={this.handlePushNotificationClick}
                >
                    {btnTitle}
                </button>
            );
        }
        return false;
    }

    renderModals() {
        const {
            email,
            emailVerification: {
                activeListings,
                inactiveListings,
                hasFeedListings,
                hasPendingEmailVerification,
            },
            location: { search },
            modalAddProperty,
            modalEnableApplications,
            modalVerifyEmail,
            modalVerifyEmailFailure,
            roles,
        } = this.props;
        const { destinationTab, totalListingsWithoutFilters } = this.state;
        const { behavior = 'Add', isOpen } = modalAddProperty;

        const queryParams = queryUtils.parse(search);
        const isPostingPathAdmin = userUtils.isPostingPathAdmin(roles);
        const fullAddress = queryParams.street ? this.getFullAddress(queryParams) : '';
        const { unit } = queryParams;

        return (
            <React.Fragment>
                <AddPropertyModal
                    isOpen={isOpen}
                    behavior={behavior}
                    address={fullAddress}
                    totalListings={totalListingsWithoutFilters}
                    destinationTab={destinationTab}
                    unit={unit}
                    preventLandingPageRedirect={hasFeedListings && hasPendingEmailVerification}
                />

                <EnableApplicationsModal
                    isOpen={modalEnableApplications.isOpen}
                    handleConfirmClick={this.handleEnableApplicationsBtnClick}
                />

                <VerifyEmailModal
                    isPostingPathAdmin={isPostingPathAdmin}
                    emailAddress={email}
                    activeListings={activeListings}
                    inactiveListings={inactiveListings}
                    isOpen={modalVerifyEmail.isOpen}
                    handleResendEmailClick={this.handleResendEmailClick}
                />

                <VerifyEmailFailureModal
                    isOpen={modalVerifyEmailFailure.isOpen}
                />
            </React.Fragment>
        );
    }

    renderListingsLoadComplete() {
        const {
            properties: { listings, pager },
            propertiesFilters: { includeArchived },
        } = this.props;
        const {
            displayTimedOutMessage, didPropertiesLoad, displayNoListingsFoundMessage, disableLoadMore,
            viewType,
        } = this.state;

        return (
            <Container
                fluidMobile={true}
                marginTop="md"
            >
                <PropertiesDisplay
                    listings={listings}
                    displayTimedOutMessage={displayTimedOutMessage}
                    displayNoListingsFoundMessage={displayNoListingsFoundMessage}
                    didPropertiesLoad={didPropertiesLoad}
                    showArchived={includeArchived}
                    viewType={viewType}
                />

                <Row>
                    <Col>
                        <Container className="InfiniteScrollerLoadMore">
                            <InfiniteScroller
                                pager={pager}
                                text="Load more properties"
                                onLoadMoreClick={this.handleLoadMoreClick}
                                disabled={disableLoadMore}
                            />
                        </Container>
                    </Col>
                </Row>
            </Container>
        );
    }

    renderMessageWithReplacedLinks(message = '', replaceText = '', replaceUrl = '') {
        return reactStringReplace(message, replaceText, () => (
            <Text
                type="body"
                inline={true}
            >
                <Link
                    type="secondary"
                    to={replaceUrl}
                    target="_blank"
                >
                    {replaceText}
                </Link>
            </Text>
        ));
    }

    renderCovid19Resources() {
        // eslint-disable-next-line max-len
        const covid19ReadOnUrl = 'https://www.zillow.com/rental-manager/resources/covid-19-landlord-resources/?itc=zw_zw_rrc_content-hubs_bn_learn-more';

        return (
            <Text fontType="body">
                Stay up-to-date with resources to support your business during COVID-19.&nbsp;
                <Link
                    type="secondary"
                    href={covid19ReadOnUrl}
                    target="_blank"
                    onClick={this.handleCovid19ReadOnUrlClick}
                >
                    Read on
                </Link>
            </Text>
        );
    }

    getConversionPercentage(count) {
        const {
            processing,
            pending,
            total,
        } = count;
        const number = ((processing + pending) / total) * 100;
        return numberUtils.asPercent(number);
    }

    renderLoadComplete() {
        const {
            email,
            properties: { numTotalListings: totalPropertiesShown },
            propertiesFilters: { includeArchived },
            paidInclusionReleaseDates,
            paidInclusionExistingStatesUpdate,
            isMultifamilyCustomer,
            isRocketshipCustomer,
            propertyConversion,
        } = this.props;

        const {
            displayPaymentsNotification,
            displayApplicationsNotification,
            displayPendingEmailVerificationBox,
            displayListingDeactivatedBox,
            displayLeasesNotification,
            displayEmailVerificationNotification,
            isDebugMode,
            isSearchBarTouched,
            emailVerificationNotificationText,
        } = this.state;

        const smColWidth = (totalPropertiesShown >= MIN_PROPERTIES_FOR_SEARCH_DISPLAY || isSearchBarTouched) ? 8 : 12;
        const displayCovid19Resources = !isMultifamilyCustomer && !isRocketshipCustomer;
        const displayListingConversionBanner = propertyConversion.count.processing > 0
            || propertyConversion.count.pending > 0;

        // first item from array (sorted by release date)
        let paidInclusionReleaseDate = null;
        let paidInclusionReleaseText = null;
        let paidInclusionFaqUrl = '';

        if (paidInclusionReleaseDates.length > 0) {
            const {
                releaseText,
                releaseDate,
                faqUrl,
            } = paidInclusionReleaseDates[0];

            paidInclusionFaqUrl = faqUrl;
            paidInclusionReleaseDate = format(new Date(releaseDate), 'MMMM D, YYYY');
            paidInclusionReleaseText = releaseText;
        }

        return (
            <div>
                {isDebugMode && (
                    <Container
                        fluidMobile={true}
                        marginBottom="sm"
                    >
                        <Row>
                            <Col xs={12}>
                                {this.renderPushNotificationBtn()}
                            </Col>
                        </Row>
                    </Container>
                )}
                {displayCovid19Resources && paidInclusionReleaseDates.length === 0 && (
                    <Container
                        className="PropertiesPage-display-container"
                        fluidMobile={true}
                        marginBottom="sm"
                    >
                        <Row>
                            <Col xs={12}>
                                <ActionMessageBox>
                                    {this.renderCovid19Resources()}
                                </ActionMessageBox>
                            </Col>
                        </Row>
                    </Container>
                )}

                {displayListingConversionBanner && (
                    <Container
                        className="PropertiesPage-display-container"
                        fluidMobile={true}
                        marginBottom="sm"
                    >
                        <Row>
                            <Col xs={12}>
                                <ActionMessageBox logoType="computer-screen">
                                    <Text fontType="body">
                                        <b>
                                            {`${this.getConversionPercentage(propertyConversion.count)} complete`}
                                        </b>
                                        &nbsp;
                                        Conversion of your listings is in progress! You can access your listings once
                                        this is complete.
                                    </Text>
                                </ActionMessageBox>
                            </Col>
                        </Row>
                    </Container>
                )}

                {displayApplicationsNotification && (
                    <Container
                        fluidMobile={true}
                        marginBottom="sm"
                    >
                        <Row>
                            <Col xs={12}>
                                <Notification type="success" onClose={this.handleNotificationCloseClick}>
                                    <Text fontType="body">
                                        Applications are enabled for your properties. You can change application
                                        preferences anytime on the property&apos;s&nbsp;
                                        <em>Applications</em>
                                        &nbsp;tab.
                                    </Text>
                                </Notification>
                            </Col>
                        </Row>
                    </Container>
                )}

                {displayPaymentsNotification && (
                    <Container
                        fluidMobile={true}
                        marginBottom="sm"
                    >
                        <Row>
                            <Col xs={12}>
                                <Notification type="success" onClose={this.handlePaymentsNotificationCloseClick}>
                                    <Text fontType="body">
                                        To manage your rent payments, click on&nbsp;
                                        <em>Payments</em>
                                        &nbsp;for any property below.
                                    </Text>
                                </Notification>
                            </Col>
                        </Row>
                    </Container>
                )}

                {displayEmailVerificationNotification && (
                    <Container
                        fluidMobile={true}
                        marginBottom="sm"
                    >
                        <Row>
                            <Col xs={12}>
                                <Notification
                                    type="success"
                                    onClose={this.handleEmailVerificationNotificationCloseClick}
                                >
                                    <Text fontType="body">
                                        {emailVerificationNotificationText }
                                    </Text>
                                </Notification>
                            </Col>
                        </Row>
                    </Container>
                )}

                {displayPendingEmailVerificationBox && (
                    <Container
                        fluidMobile={true}
                        marginBottom="sm"
                    >
                        <Row>
                            <Col>
                                <PendingEmailVerificationBox
                                    emailAddress={email}
                                    handleViewDetailsClick={this.showVerifyEmailModal}
                                />
                            </Col>
                        </Row>
                    </Container>
                )}

                {displayListingDeactivatedBox && (
                    <Container
                        fluidMobile={true}
                        marginBottom="sm"
                    >
                        <Row>
                            <Col xs={12}>
                                <ActionMessageBox>
                                Your listing has been deactivated.
                                </ActionMessageBox>
                            </Col>
                        </Row>
                    </Container>
                )}

                {displayLeasesNotification && (
                    <Container
                        fluidMobile={true}
                        marginBottom="sm"
                    >
                        <Row>
                            <Col xs={12}>
                                <Notification type="success" onClose={this.handleLeasesNotificationCloseClick}>
                                    <Text fontType="body">
                                        For your properties with a&nbsp;
                                        <em>Leases</em>
                                        &nbsp;tab, click on it to create a custom lease.
                                    </Text>
                                </Notification>
                            </Col>
                        </Row>
                    </Container>
                )}

                {paidInclusionExistingStatesUpdate.message && (
                    <Container
                        fluidMobile={true}
                        marginBottom="sm"
                    >
                        <Row>
                            <Col xs={12}>
                                <ActionMessageBox data-test="paid-inclusion-existing-states-update-banner">
                                    {this.renderMessageWithReplacedLinks(
                                        paidInclusionExistingStatesUpdate.message,
                                        paidInclusionExistingStatesUpdate.replaceText,
                                        paidInclusionExistingStatesUpdate.replaceUrl
                                    )
                                    }
                                </ActionMessageBox>
                            </Col>
                        </Row>
                    </Container>
                )}

                {/* https://jira.hotterpads.com/browse/ZRM-4152 */}
                {paidInclusionReleaseDates.length > 0 && (
                    <Container
                        fluidMobile={true}
                        marginBottom="sm"
                    >
                        <Row>
                            <Col xs={12}>
                                <ActionMessageBox data-test="paid-inclusion-banner">
                                    {paidInclusionReleaseText}
                                    {paidInclusionReleaseDate}
                                    .&nbsp;
                                    <Link
                                        type="secondary"
                                        href={paidInclusionFaqUrl}
                                        target="_blank"
                                    >
                                        Learn more
                                    </Link>
                                    {displayCovid19Resources && (
                                        this.renderCovid19Resources()
                                    )}
                                </ActionMessageBox>
                            </Col>
                        </Row>
                    </Container>
                )}

                {this.renderListingsLoadComplete()}

                {/* archived link */}
                <Container
                    marginTop="sm"
                    className="PropertiesPage-ArchiveToggleLink"
                >
                    <Row marginBottom="sm">
                        <Col sm={smColWidth}>
                            <Link
                                type="secondary"
                                onClick={this.toggleArchivedView}
                                className="PropertiesPage-archiveLink"
                            >
                                {includeArchived ?
                                    'View for rent & off market properties' : 'View archived properties'
                                }
                            </Link>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }

    render() {
        const { brand } = this.context;

        const {
            maintenancePartner,
            propertiesFilters: { includeArchived },
            isUserBlocked,
            isSOH,
        } = this.props;

        const {
            listingsLoading,
            viewType,
        } = this.state;

        const paths = pathsManifest.getAllPaths();
        const pageTitle = includeArchived ? 'Archived Properties' : 'Properties';
        const displayMaintenanceBanner = brand === Constants.UNFORMATTED_BRAND_NAMES.zillow &&
            maintenancePartner && maintenancePartner.isAvailable;

        const classNames = cx('PropertiesPage', {
            SOH: isSOH,
        });

        if (this.needsRedirect() && !isUserBlocked) {
            return <Redirect to={paths.base} />;
        }

        return (
            <AuthCheckWrapper requireStrongAuthentication={false}>
                <div className={classNames}>
                    <div>
                        <HeadWrapper
                            title={pageTitle}
                        />

                        {!listingsLoading && this.renderModals()}

                        {this.renderPageHeading()}
                        {this.renderSubHeadingSection()}

                        {listingsLoading
                            ? <PropertiesPageSkeleton viewType={viewType} />
                            : this.renderLoadComplete()
                        }

                        {displayMaintenanceBanner &&
                            this.renderMaintenanceBanner()
                        }
                    </div>
                </div>
            </AuthCheckWrapper>
        );
    }
}

const mapStateToProps = state => ({
    cookieEnabledServices: AppSelectors.makeCookieEnabledServices(state),
    isScreenTabletOrDesktop: AppSelectors.makeIsScreenTabletOrDesktop(state),
    isSOH: AppSelectors.makeIsSOH(state),
    email: UserSelectors.makeEmail(state),
    emailVerification: EmailVerificationSelectors.makeEmailVerification(state),
    isUserBlocked: UserSelectors.makeIsUserBlocked(state),
    location: RoutingSelectors.makeLocation(state),
    modalAddProperty: ModalSelectors.makeAddPropertyModal(state),
    modalEnableApplications: ModalSelectors.makeEnableApplicationsModal(state),
    modalVerifyEmail: ModalSelectors.makeVerifyEmailModal(state),
    modalVerifyEmailFailure: ModalSelectors.makeVerifyEmailFailureModal(state),
    maintenancePartner: PropertiesSelectors.makeMaintenancePartner(state),
    numTotalListings: PropertiesSelectors.makeNumTotalListings(state),
    pager: PropertiesSelectors.makePager(state),
    properties: PropertiesSelectors.makeProperties(state),
    propertyConversion: PropertiesSelectors.makePropertyConversion(state),
    roles: UserSelectors.makeRoles(state),
    propertiesFilters: PropertiesSelectors.makePropertiesFilters(state),
    paidInclusionReleaseDates: PropertiesSelectors.makePaidInclusionReleaseDates(state),
    paidInclusionExistingStatesUpdate: PropertiesSelectors.makePaidInclusionExistingStatesUpdate(state),
    user: UserSelectors.makeUser(state),
    prevPath: AppSelectors.makePrevPath(state),
    isMultifamilyCustomer: UserSelectors.makeIsMultifamilyCustomer(state),
    isRocketshipCustomer: UserSelectors.makeIsRocketshipCustomer(state),
});

export { PropertiesPage };
export default withErrorBoundary(connect(mapStateToProps)(PropertiesPage));
